import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import {
  GraduationCap, Loader2, User, Lock, Eye, EyeOff, ArrowRight, ShieldCheck, Users,
  Users2, BarChart3, FileText, IndianRupee, Settings2, Lightbulb, Target, BookOpen, Crown,
  Fingerprint, KeyRound, Mail, MessageSquare,
} from "lucide-react";
import { toast } from "sonner";
import { startAuthentication } from "@simplewebauthn/browser";

const FEATURES = [
  { icon: Users2, label: "Student\nManagement", color: "#2563eb" },
  { icon: GraduationCap, label: "Academic\nManagement", color: "#16a34a" },
  { icon: BarChart3, label: "Attendance\nTracking", color: "#f97316" },
  { icon: FileText, label: "Examination\n& Results", color: "#e11d48" },
  { icon: IndianRupee, label: "Fee\nManagement", color: "#9333ea" },
  { icon: Settings2, label: "Administration", color: "#0891b2" },
];

const VALUES = [
  { icon: Lightbulb, label: "Learn More" },
  { icon: BarChart3, label: "Grow Together" },
  { icon: Users, label: "Build Character" },
  { icon: Target, label: "Achieve Greater" },
];

export default function Login() {
  const { login, applySession, user } = useAuth();
  const nav = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [school, setSchool] = useState({ name: "SSS PUBLIC SCHOOL", tag_line: "JAITHRA-ETAH", logo: "" });
  const [method, setMethod] = useState("password");
  const [otpStep, setOtpStep] = useState(false);
  const [otpChannel, setOtpChannel] = useState("email");
  const [otpCode, setOtpCode] = useState("");
  const [otpSentTo, setOtpSentTo] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);

  useEffect(() => { if (user) nav("/dashboard"); }, [user, nav]);
  useEffect(() => {
    api.get("/public/school-info").then((r) => { if (r.data?.name) setSchool(r.data); }).catch(() => {});
    // Only the username may be remembered (never the password); a new device shows nothing.
    const saved = localStorage.getItem("remember_user");
    if (saved) { setUsername(saved); setRemember(true); }
    if (new URLSearchParams(window.location.search).get("timeout")) {
      toast.info("You were signed out after 15 minutes of inactivity. Please log in again.");
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const u = await login(username, password);
      if (remember) localStorage.setItem("remember_user", username); else localStorage.removeItem("remember_user");
      localStorage.setItem("fp_prompt_pending", "1");
      toast.success(`Welcome back, ${u.name}`);
      nav("/dashboard");
    } catch (e) {
      setErr(apiErr(e.response?.data?.detail) || "Login failed");
    } finally { setLoading(false); }
  };

  const doForgot = async () => {
    try { await api.post("/auth/forgot-password", { username }); toast.success("If the account exists, reset instructions were sent."); setForgot(false); }
    catch (e) { toast.error("Try again"); }
  };

  const doFingerprint = async () => {
    if (!username.trim()) { setErr("Enter your username first"); return; }
    setErr(""); setLoading(true);
    try {
      const opts = (await api.post("/webauthn/login/options", { username })).data;
      const cred = await startAuthentication({ optionsJSON: opts });
      const { data } = await api.post("/webauthn/login/verify", { username, credential: cred });
      if (remember) localStorage.setItem("remember_user", username); else localStorage.removeItem("remember_user");
      applySession(data);
      toast.success(`Welcome back, ${data.user.name}`);
      nav("/dashboard");
    } catch (e) {
      const msg = e?.name === "NotAllowedError" ? "Cancelled or no matching fingerprint on this device." : (apiErr(e?.response?.data?.detail) || e.message || "Fingerprint login failed");
      setErr(msg);
    } finally { setLoading(false); }
  };

  const sendOtp = async () => {
    if (!username.trim()) { setErr("Enter your username first"); return; }
    setErr(""); setOtpLoading(true);
    try {
      const { data } = await api.post("/auth/otp/request", { username, channel: otpChannel });
      setOtpSentTo(data.to); setOtpStep(true); setOtpCode("");
      toast.success(`Code sent to ${data.to}`);
    } catch (e) { setErr(apiErr(e?.response?.data?.detail) || "Could not send code"); }
    finally { setOtpLoading(false); }
  };

  const verifyOtp = async () => {
    if (!otpCode.trim()) { setErr("Enter the code"); return; }
    setErr(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/otp/verify", { username, code: otpCode });
      if (remember) localStorage.setItem("remember_user", username); else localStorage.removeItem("remember_user");
      applySession(data);
      toast.success(`Welcome back, ${data.user.name}`);
      nav("/dashboard");
    } catch (e) { setErr(apiErr(e?.response?.data?.detail) || "Invalid code"); }
    finally { setLoading(false); }
  };

  const Emblem = ({ size = 88 }) => (
    school.logo
      ? <img src={school.logo} alt="School logo" style={{ width: size, height: size }} className="object-contain drop-shadow" />
      : <div style={{ width: size, height: size }} className="rounded-2xl bg-gradient-to-br from-amber-400 to-red-600 flex items-center justify-center text-white shadow-lg ring-4 ring-white/60"><GraduationCap size={size * 0.55} /></div>
  );

  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-sky-100 via-blue-50 to-blue-100 flex flex-col" data-testid="login-page">
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1.35fr_1fr] gap-0">
        {/* Left brand panel */}
        <div className="relative hidden lg:flex flex-col px-10 pt-8 overflow-hidden">
          <div className="absolute top-6 right-8 text-right z-10">
            
          </div>

          <div className="flex items-center gap-4 relative z-10">
            <Emblem size={96} />
            <div>
              <h1 className="font-heading text-4xl xl:text-5xl font-extrabold text-blue-900 tracking-tight leading-none">{school.name}</h1>
              {school.tag_line && <p className="font-heading text-2xl font-bold text-red-700 tracking-wide mt-1">{school.tag_line}</p>}
              <div className="mt-2 border-t-2 border-red-300 pt-1.5 text-sm font-semibold tracking-[0.2em] text-blue-800">DISCIPLINE • KNOWLEDGE • BRIGHT FUTURE</div>
            </div>
          </div>

          <div className="grid grid-cols-6 gap-3 mt-8 relative z-10">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.label} className="flex flex-col items-center text-center gap-2">
                  <div className="w-14 h-14 rounded-full flex items-center justify-center text-white shadow-md" style={{ backgroundColor: f.color }}><Icon size={24} /></div>
                  <span className="text-xs font-semibold text-slate-700 leading-tight whitespace-pre-line">{f.label}</span>
                </div>
              );
            })}
          </div>

          <div className="mt-8 relative z-10">
            <p className="font-serif text-2xl xl:text-3xl">
              <span className="text-blue-900 font-bold italic">“A Better School </span>
              <span className="text-amber-500 font-bold italic">for a Brighter Tomorrow”</span>
            </p>
          </div>

          <div className="mt-auto pb-8 relative z-10">
            <div className="rounded-2xl bg-white/50 backdrop-blur border border-white/60 p-6">
              <div className="flex items-start gap-3">
                <BookOpen size={30} className="text-amber-500 shrink-0 mt-1" />
                <div>
                  <p className="font-serif italic text-lg text-blue-900 leading-snug">"Education is not just about learning, but about building a better society."</p>
                  <div className="h-1 w-40 mt-2 rounded-full bg-amber-400" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right login card */}
        <div className="flex items-center justify-center p-5 sm:p-8">
          <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-7 sm:p-9 border border-white/60">
            <div className="flex flex-col items-center text-center">
              <Emblem size={84} />
              <h2 className="font-heading text-4xl font-extrabold text-blue-900 mt-3">Welcome</h2>
              <p className="text-slate-500 text-sm mt-1">Login to your School Management System</p>
            </div>

            <div className="mt-5 grid grid-cols-3 gap-2" data-testid="login-method-switch">
              {[["password", "Password", Lock], ["fingerprint", "Fingerprint", Fingerprint], ["otp", "OTP", KeyRound]].map(([k, label, Icon]) => (
                <button key={k} type="button" onClick={() => { setMethod(k); setErr(""); setOtpStep(false); }} data-testid={`login-method-${k}`}
                  className={`flex flex-col items-center gap-1 py-2.5 rounded-xl border text-xs font-semibold transition ${method === k ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
                  <Icon size={18} />{label}
                </button>
              ))}
            </div>

            {method === "password" && (
            <form onSubmit={submit} className="mt-6 space-y-4" autoComplete="off">
              <div className="relative">
                <User size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-blue-500" />
                <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Username" autoFocus data-testid="username-input" autoComplete="username"
                  className="w-full h-12 pl-11 pr-4 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition" />
              </div>
              <div className="relative">
                <Lock size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-blue-500" />
                <input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" data-testid="password-input" autoComplete="new-password"
                  className="w-full h-12 pl-11 pr-11 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition" />
                <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="toggle-password" aria-label="Toggle password">
                  {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              <div className="flex items-center justify-between text-sm">
                <label className="flex items-center gap-2 text-slate-600 cursor-pointer">
                  <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="w-4 h-4 rounded border-slate-300 accent-blue-600" data-testid="remember-me" />
                  Remember Me
                </label>
                <button type="button" onClick={() => setForgot(true)} className="text-blue-600 hover:underline font-medium" data-testid="forgot-link">Forgot Password?</button>
              </div>

              {err && <div className="text-sm text-rose-600 bg-rose-50 rounded-lg px-3 py-2" data-testid="login-error">{err}</div>}

              <button type="submit" disabled={loading} data-testid="login-submit"
                className="w-full h-12 rounded-xl bg-gradient-to-r from-blue-800 to-blue-600 text-white font-bold text-lg flex items-center justify-center gap-2 shadow-lg hover:from-blue-900 hover:to-blue-700 transition disabled:opacity-70">
                {loading ? <Loader2 className="animate-spin" size={20} /> : <><ArrowRight size={20} /> Login</>}
              </button>
            </form>
            )}

            {method === "fingerprint" && (
              <div className="mt-6 space-y-4" data-testid="fingerprint-panel">
                <div className="relative">
                  <User size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-blue-500" />
                  <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Username" autoComplete="username" data-testid="username-input"
                    className="w-full h-12 pl-11 pr-4 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition" />
                </div>
                {err && <div className="text-sm text-rose-600 bg-rose-50 rounded-lg px-3 py-2" data-testid="login-error">{err}</div>}
                <button type="button" onClick={doFingerprint} disabled={loading} data-testid="fingerprint-login-btn"
                  className="w-full h-12 rounded-xl bg-gradient-to-r from-blue-800 to-blue-600 text-white font-bold text-lg flex items-center justify-center gap-2 shadow-lg hover:from-blue-900 hover:to-blue-700 transition disabled:opacity-70">
                  {loading ? <Loader2 className="animate-spin" size={20} /> : <><Fingerprint size={20} /> Use Fingerprint / Face</>}
                </button>
                <p className="text-xs text-center text-slate-400">First time? Log in with your password, then turn on fingerprint in Settings → Login.</p>
              </div>
            )}

            {method === "otp" && (
              <div className="mt-6 space-y-4" data-testid="otp-panel">
                <div className="relative">
                  <User size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-blue-500" />
                  <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Username" autoComplete="username" data-testid="username-input" disabled={otpStep}
                    className="w-full h-12 pl-11 pr-4 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-blue-500 outline-none transition disabled:opacity-60" />
                </div>
                {!otpStep && (
                  <div className="grid grid-cols-2 gap-2" data-testid="otp-channel-switch">
                    {[["email", "Email", Mail], ["sms", "SMS", MessageSquare]].map(([k, label, Icon]) => (
                      <button key={k} type="button" onClick={() => setOtpChannel(k)} data-testid={`otp-channel-${k}`}
                        className={`flex items-center justify-center gap-2 h-11 rounded-xl border text-sm font-semibold transition ${otpChannel === k ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
                        <Icon size={16} />{label}
                      </button>
                    ))}
                  </div>
                )}
                {otpStep && (
                  <div className="relative">
                    <KeyRound size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-blue-500" />
                    <input value={otpCode} onChange={(e) => setOtpCode(e.target.value)} placeholder="Enter 6-digit code" inputMode="numeric" maxLength={6} data-testid="otp-code-input"
                      className="w-full h-12 pl-11 pr-4 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-blue-500 outline-none transition tracking-[0.3em] font-bold" />
                  </div>
                )}
                {otpStep && otpSentTo && <p className="text-xs text-slate-500">Code sent to <b>{otpSentTo}</b>. It expires in 5 minutes.</p>}
                {err && <div className="text-sm text-rose-600 bg-rose-50 rounded-lg px-3 py-2" data-testid="login-error">{err}</div>}
                {!otpStep ? (
                  <button type="button" onClick={sendOtp} disabled={otpLoading} data-testid="otp-send-btn"
                    className="w-full h-12 rounded-xl bg-gradient-to-r from-blue-800 to-blue-600 text-white font-bold text-lg flex items-center justify-center gap-2 shadow-lg hover:from-blue-900 hover:to-blue-700 transition disabled:opacity-70">
                    {otpLoading ? <Loader2 className="animate-spin" size={20} /> : <><ArrowRight size={20} /> Send Code</>}
                  </button>
                ) : (
                  <div className="space-y-2">
                    <button type="button" onClick={verifyOtp} disabled={loading} data-testid="otp-verify-btn"
                      className="w-full h-12 rounded-xl bg-gradient-to-r from-blue-800 to-blue-600 text-white font-bold text-lg flex items-center justify-center gap-2 shadow-lg hover:from-blue-900 hover:to-blue-700 transition disabled:opacity-70">
                      {loading ? <Loader2 className="animate-spin" size={20} /> : <><ArrowRight size={20} /> Verify & Login</>}
                    </button>
                    <button type="button" onClick={() => { setOtpStep(false); setOtpCode(""); setErr(""); }} className="w-full text-sm text-slate-500 hover:underline" data-testid="otp-back-btn">Back / resend code</button>
                  </div>
                )}
              </div>
            )}

            {forgot && (
              <div className="mt-4 p-4 rounded-lg bg-amber-50 border border-amber-200 text-sm">
                <p className="text-amber-800 mb-2">Enter your username and we'll send reset instructions.</p>
                <button onClick={doForgot} className="px-3 py-1.5 rounded-lg border border-amber-300 bg-white text-amber-800 font-medium" data-testid="send-reset-btn">Send Reset</button>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-blue-950 to-blue-800 text-white">
        <div className="max-w-6xl mx-auto px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="grid grid-cols-4 gap-6 sm:gap-10">
            {VALUES.map((v) => { const Icon = v.icon; return (
              <div key={v.label} className="flex flex-col items-center gap-1 text-center">
                <Icon size={22} className="text-amber-300" />
                <span className="text-xs font-medium leading-tight">{v.label}</span>
              </div>
            ); })}
          </div>
          <div className="flex items-center gap-2 bg-amber-400 text-blue-900 font-serif italic font-bold px-5 py-2 rounded-full shadow">
            <GraduationCap size={20} /> Education Today · A Better Tomorrow
          </div>
        </div>
      </footer>
    </div>
  );
}
